/**
 * Eric / Vincent (2026-05-14, incident login Vincent prod) — Centralized API
 * base URL helper.
 *
 * WHY THIS EXISTS
 * ───────────────
 * In Emergent's deployment model, `REACT_APP_BACKEND_URL` is baked into the
 * production bundle at build time. For `knoria.com` (the customer custom
 * domain), this value is `https://procedures-1.emergent.host` — a DIFFERENT
 * origin than the page itself.
 *
 * That cross-origin setup works for permissive browsers/networks but fails
 * silently for users with:
 *   • Safari Intelligent Tracking Prevention (strips 3rd-party cookies)
 *   • Brave Shields / uBlock Origin (blocks emergent.host as "tracker")
 *   • Firefox strict / Total Cookie Protection
 *   • Corporate firewalls / VPNs that allowlist knoria.com but block emergent.host
 *   • Slow DNS resolution on the secondary domain
 *
 * Vincent (the QA dev) hit exactly this issue on 2026-05-14 — all 4 connection
 * probes failed because his browser blocked the cross-origin fetches. This
 * file fixes that by transparently routing API calls back to the SAME origin
 * the page was served from, when (and only when) the user is on the customer
 * custom domain.
 *
 * HOW
 * ───
 * • If the page is loaded from a domain that does NOT contain
 *   "preview.emergentagent.com" → use `window.location.origin` (same-origin →
 *   knoria.com/api/... routed by Cloudflare/ingress to the backend).
 * • Otherwise → use the env var (preview behaviour, unchanged).
 *
 * The platform's `/api/*` ingress rule guarantees same-origin works for the
 * production custom domain. The env var stays the source of truth for preview
 * and Server-Side rendering / non-browser contexts.
 */

function _computeApiBase() {
  // Browser context only — Node/SSR has no `window`.
  const hasWindow = typeof window !== "undefined" && window.location;

  // Eric / Vincent (2026-05-14, login incident, Defense Layer 1) —
  // Runtime config override. `/public/runtime-config.js` is loaded BEFORE the
  // bundle and may set `window.KNORIA_CONFIG.api_base`. If non-empty, it wins
  // over everything else. This lets the operator hot-fix the API URL by editing
  // a single static file on the CDN without rebuilding the bundle.
  if (hasWindow && window.KNORIA_CONFIG && typeof window.KNORIA_CONFIG.api_base === "string" && window.KNORIA_CONFIG.api_base.length > 0) {
    return window.KNORIA_CONFIG.api_base;
  }

  // Default: env var (used by preview, SSR, tests).
  const envUrl = (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) || "";

  if (!hasWindow) {
    return envUrl;
  }

  const origin = window.location.origin || "";
  const host = window.location.hostname || "";

  // Preview environment: use the env var verbatim (already same-origin in practice).
  if (host.endsWith(".preview.emergentagent.com")) {
    return envUrl;
  }

  // Localhost dev: use env var (typically http://localhost:8001 via proxy or absolute).
  if (host === "localhost" || host === "127.0.0.1" || host.endsWith(".local")) {
    return envUrl;
  }

  // Any other host (custom domain like knoria.com OR emergent.host fallback)
  // → use same-origin to avoid cross-origin pitfalls.
  return origin;
}

// Computed once at module load — value is stable for the lifetime of the page.
export const API_BASE = _computeApiBase();

// Convenience helper for runtime debugging from devtools.
if (typeof window !== "undefined") {
  // eslint-disable-next-line no-underscore-dangle
  window.__KNORIA_API_BASE__ = API_BASE;
}
